import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Save, Building, Clock, Palette, Bell, Shield, MapPin } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useCompanyData, useUpdateCompanyData } from "@/hooks/use-company-data";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { formatCEP, formatPhone, validateEmail } from "@/lib/validations";

export default function CompanySettingsPage() {
  const { slug } = useParams();
  const { companies } = useAuth();
  const { toast } = useToast();
  const company = companies?.find(c => c.slug === slug);
  const { data: companyData, isLoading } = useCompanyData(company?.id);
  const updateCompany = useUpdateCompanyData();

  const [settings, setSettings] = useState({
    name: "",
    razao_social: "",
    nome_fantasia: "",
    slug: "",
    doc_cnpj: "",
    email: "",
    telefone: "",
    endereco_logradouro: "",
    endereco_numero: "",
    endereco_complemento: "",
    endereco_bairro: "",
    endereco_cidade: "",
    endereco_estado: "",
    endereco_cep: "",
    timezone: "America/Sao_Paulo",
    notifications: {
      email: true,
      licenseExpiry: true,
      systemUpdates: false,
      userActivity: true,
    },
    security: {
      twoFactorRequired: false,
      sessionTimeout: "8",
      passwordComplexity: true,
    },
    branding: {
      primaryColor: "#0066cc",
      logo: "",
      favicon: "",
    }
  });

  // Update settings when company data is loaded
  useEffect(() => {
    if (companyData) {
      setSettings(prev => ({
        ...prev,
        name: companyData.name || "",
        razao_social: companyData.razao_social || "",
        nome_fantasia: companyData.nome_fantasia || "",
        slug: companyData.slug || "",
        doc_cnpj: companyData.doc_cnpj || "",
        email: companyData.email || "",
        telefone: companyData.telefone || "",
        endereco_logradouro: companyData.endereco_logradouro || "",
        endereco_numero: companyData.endereco_numero || "",
        endereco_complemento: companyData.endereco_complemento || "",
        endereco_bairro: companyData.endereco_bairro || "",
        endereco_cidade: companyData.endereco_cidade || "",
        endereco_estado: companyData.endereco_estado || "",
        endereco_cep: companyData.endereco_cep || "",
        timezone: companyData.timezone || "America/Sao_Paulo",
        branding: {
          ...prev.branding,
          ...companyData.branding,
        }
      }));
    }
  }, [companyData]);

  const handleSave = async () => {
    if (!validateEmail(settings.email) && settings.email) {
      toast({
        variant: "destructive",
        title: "Email inválido",
        description: "Por favor, insira um email válido.",
      });
      return;
    }

    try {
      await updateCompany.mutateAsync({
        name: settings.name,
        razao_social: settings.razao_social,
        nome_fantasia: settings.nome_fantasia,
        doc_cnpj: settings.doc_cnpj,
        email: settings.email,
        telefone: settings.telefone,
        endereco_logradouro: settings.endereco_logradouro,
        endereco_numero: settings.endereco_numero,
        endereco_complemento: settings.endereco_complemento,
        endereco_bairro: settings.endereco_bairro,
        endereco_cidade: settings.endereco_cidade,
        endereco_estado: settings.endereco_estado,
        endereco_cep: settings.endereco_cep,
        timezone: settings.timezone,
        branding: settings.branding,
      });

      toast({
        title: "Configurações salvas",
        description: "As configurações da empresa foram atualizadas com sucesso.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao salvar",
        description: "Ocorreu um erro ao salvar as configurações.",
      });
    }
  };

  if (isLoading) {
    return <div>Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Configurações</h1>
          <p className="text-muted-foreground">
            Gerencie as configurações da empresa {company?.name}
          </p>
        </div>
        <Button onClick={handleSave} disabled={updateCompany.isPending}>
          <Save className="h-4 w-4 mr-2" />
          {updateCompany.isPending ? "Salvando..." : "Salvar Alterações"}
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Company Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Building className="h-5 w-5 mr-2" />
              Informações da Empresa
            </CardTitle>
            <CardDescription>
              Informações básicas sobre sua empresa
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="company-name">Nome da Empresa</Label>
                <Input
                  id="company-name"
                  value={settings.name}
                  onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Nome usado internamente no sistema
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-slug">Slug da Empresa</Label>
                <Input
                  id="company-slug"
                  value={settings.slug}
                  onChange={(e) => setSettings({ ...settings, slug: e.target.value })}
                  disabled
                />
                <p className="text-xs text-muted-foreground">
                  O slug não pode ser alterado após a criação
                </p>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="razao-social">Razão Social</Label>
                <Input
                  id="razao-social"
                  value={settings.razao_social}
                  onChange={(e) => setSettings({ ...settings, razao_social: e.target.value })}
                  placeholder="Nome legal da empresa"
                />
                <p className="text-xs text-muted-foreground">
                  Nome jurídico registrado
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nome-fantasia">Nome Fantasia</Label>
                <Input
                  id="nome-fantasia"
                  value={settings.nome_fantasia}
                  onChange={(e) => setSettings({ ...settings, nome_fantasia: e.target.value })}
                  placeholder="Nome comercial da empresa"
                />
                <p className="text-xs text-muted-foreground">
                  Nome comercial usado em documentos e relatórios
                </p>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="company-cnpj">CNPJ</Label>
                <Input
                  id="company-cnpj"
                  value={settings.doc_cnpj}
                  onChange={(e) => setSettings({ ...settings, doc_cnpj: e.target.value })}
                  placeholder="00.000.000/0001-00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company-email">Email</Label>
                <Input
                  id="company-email"
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  placeholder="contato@empresa.com"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="company-phone">Telefone</Label>
              <Input
                id="company-phone"
                value={settings.telefone}
                onChange={(e) => {
                  const formatted = formatPhone(e.target.value);
                  setSettings({ ...settings, telefone: formatted });
                }}
                placeholder="(11) 99999-9999"
              />
            </div>
          </CardContent>
        </Card>

        {/* Address Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              Endereço da Empresa
            </CardTitle>
            <CardDescription>
              Informações de endereço e localização
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="endereco-logradouro">Logradouro</Label>
                <Input
                  id="endereco-logradouro"
                  value={settings.endereco_logradouro}
                  onChange={(e) => setSettings({ ...settings, endereco_logradouro: e.target.value })}
                  placeholder="Rua, Avenida, etc."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endereco-numero">Número</Label>
                <Input
                  id="endereco-numero"
                  value={settings.endereco_numero}
                  onChange={(e) => setSettings({ ...settings, endereco_numero: e.target.value })}
                  placeholder="123"
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="endereco-complemento">Complemento</Label>
                <Input
                  id="endereco-complemento"
                  value={settings.endereco_complemento}
                  onChange={(e) => setSettings({ ...settings, endereco_complemento: e.target.value })}
                  placeholder="Andar, Sala, etc."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endereco-bairro">Bairro</Label>
                <Input
                  id="endereco-bairro"
                  value={settings.endereco_bairro}
                  onChange={(e) => setSettings({ ...settings, endereco_bairro: e.target.value })}
                  placeholder="Centro"
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="endereco-cidade">Cidade</Label>
                <Input
                  id="endereco-cidade"
                  value={settings.endereco_cidade}
                  onChange={(e) => setSettings({ ...settings, endereco_cidade: e.target.value })}
                  placeholder="São Paulo"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endereco-estado">Estado</Label>
                <Select value={settings.endereco_estado} onValueChange={(value) => setSettings({ ...settings, endereco_estado: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AC">Acre</SelectItem>
                    <SelectItem value="AL">Alagoas</SelectItem>
                    <SelectItem value="AP">Amapá</SelectItem>
                    <SelectItem value="AM">Amazonas</SelectItem>
                    <SelectItem value="BA">Bahia</SelectItem>
                    <SelectItem value="CE">Ceará</SelectItem>
                    <SelectItem value="DF">Distrito Federal</SelectItem>
                    <SelectItem value="ES">Espírito Santo</SelectItem>
                    <SelectItem value="GO">Goiás</SelectItem>
                    <SelectItem value="MA">Maranhão</SelectItem>
                    <SelectItem value="MT">Mato Grosso</SelectItem>
                    <SelectItem value="MS">Mato Grosso do Sul</SelectItem>
                    <SelectItem value="MG">Minas Gerais</SelectItem>
                    <SelectItem value="PA">Pará</SelectItem>
                    <SelectItem value="PB">Paraíba</SelectItem>
                    <SelectItem value="PR">Paraná</SelectItem>
                    <SelectItem value="PE">Pernambuco</SelectItem>
                    <SelectItem value="PI">Piauí</SelectItem>
                    <SelectItem value="RJ">Rio de Janeiro</SelectItem>
                    <SelectItem value="RN">Rio Grande do Norte</SelectItem>
                    <SelectItem value="RS">Rio Grande do Sul</SelectItem>
                    <SelectItem value="RO">Rondônia</SelectItem>
                    <SelectItem value="RR">Roraima</SelectItem>
                    <SelectItem value="SC">Santa Catarina</SelectItem>
                    <SelectItem value="SP">São Paulo</SelectItem>
                    <SelectItem value="SE">Sergipe</SelectItem>
                    <SelectItem value="TO">Tocantins</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="endereco-cep">CEP</Label>
                <Input
                  id="endereco-cep"
                  value={settings.endereco_cep}
                  onChange={(e) => {
                    const formatted = formatCEP(e.target.value);
                    setSettings({ ...settings, endereco_cep: formatted });
                  }}
                  placeholder="00000-000"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Regional Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              Configurações Regionais
            </CardTitle>
            <CardDescription>
              Fuso horário e configurações de localização
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="timezone">Fuso Horário</Label>
              <Select value={settings.timezone} onValueChange={(value) => setSettings({ ...settings, timezone: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/Sao_Paulo">Brasília (UTC-3)</SelectItem>
                  <SelectItem value="America/Manaus">Manaus (UTC-4)</SelectItem>
                  <SelectItem value="America/Rio_Branco">Rio Branco (UTC-5)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Bell className="h-5 w-5 mr-2" />
              Configurações de Notificação
            </CardTitle>
            <CardDescription>
              Configure quando e como receber notificações
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Notificações por Email</Label>
                <p className="text-sm text-muted-foreground">
                  Receber notificações importantes por email
                </p>
              </div>
              <Switch
                checked={settings.notifications.email}
                onCheckedChange={(checked) =>
                  setSettings({
                    ...settings,
                    notifications: { ...settings.notifications, email: checked }
                  })
                }
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Renovação de Planos</Label>
                <p className="text-sm text-muted-foreground">
                  Alertas sobre planos próximos do vencimento
                </p>
              </div>
              <Switch
                checked={settings.notifications.licenseExpiry}
                onCheckedChange={(checked) =>
                  setSettings({
                    ...settings,
                    notifications: { ...settings.notifications, licenseExpiry: checked }
                  })
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Atualizações do Sistema</Label>
                <p className="text-sm text-muted-foreground">
                  Notificações sobre novas funcionalidades
                </p>
              </div>
              <Switch
                checked={settings.notifications.systemUpdates}
                onCheckedChange={(checked) =>
                  setSettings({
                    ...settings,
                    notifications: { ...settings.notifications, systemUpdates: checked }
                  })
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Atividade de Usuários</Label>
                <p className="text-sm text-muted-foreground">
                  Relatórios de atividade dos usuários
                </p>
              </div>
              <Switch
                checked={settings.notifications.userActivity}
                onCheckedChange={(checked) =>
                  setSettings({
                    ...settings,
                    notifications: { ...settings.notifications, userActivity: checked }
                  })
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="h-5 w-5 mr-2" />
              Configurações de Segurança
            </CardTitle>
            <CardDescription>
              Políticas de segurança e autenticação
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Autenticação de Dois Fatores</Label>
                <p className="text-sm text-muted-foreground">
                  Exigir 2FA para todos os usuários
                </p>
              </div>
              <Switch
                checked={settings.security.twoFactorRequired}
                onCheckedChange={(checked) =>
                  setSettings({
                    ...settings,
                    security: { ...settings.security, twoFactorRequired: checked }
                  })
                }
              />
            </div>
            
            <Separator />
            
            <div className="space-y-2">
              <Label htmlFor="session-timeout">Timeout de Sessão (horas)</Label>
              <Select 
                value={settings.security.sessionTimeout} 
                onValueChange={(value) => 
                  setSettings({
                    ...settings,
                    security: { ...settings.security, sessionTimeout: value }
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 hora</SelectItem>
                  <SelectItem value="4">4 horas</SelectItem>
                  <SelectItem value="8">8 horas</SelectItem>
                  <SelectItem value="24">24 horas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Complexidade de Senha</Label>
                <p className="text-sm text-muted-foreground">
                  Exigir senhas complexas
                </p>
              </div>
              <Switch
                checked={settings.security.passwordComplexity}
                onCheckedChange={(checked) =>
                  setSettings({
                    ...settings,
                    security: { ...settings.security, passwordComplexity: checked }
                  })
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Branding Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Palette className="h-5 w-5 mr-2" />
              Personalização Visual
            </CardTitle>
            <CardDescription>
              Customize a aparência dos sistemas para sua empresa
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="primary-color">Cor Primária</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="primary-color"
                  type="color"
                  value={settings.branding.primaryColor}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      branding: { ...settings.branding, primaryColor: e.target.value }
                    })
                  }
                  className="w-16 h-10"
                />
                <Input
                  value={settings.branding.primaryColor}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      branding: { ...settings.branding, primaryColor: e.target.value }
                    })
                  }
                  className="font-mono"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="logo-url">URL do Logo</Label>
              <Input
                id="logo-url"
                value={settings.branding.logo}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    branding: { ...settings.branding, logo: e.target.value }
                  })
                }
                placeholder="https://exemplo.com/logo.png"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="favicon-url">URL do Favicon</Label>
              <Input
                id="favicon-url"
                value={settings.branding.favicon}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    branding: { ...settings.branding, favicon: e.target.value }
                  })
                }
                placeholder="https://exemplo.com/favicon.ico"
              />
            </div>
          </CardContent>
        </Card>

        {/* Status Card */}
        <Card>
          <CardHeader>
            <CardTitle>Status da Empresa</CardTitle>
            <CardDescription>
              Informações sobre o status atual da empresa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Status Atual</div>
                <div className="text-sm text-muted-foreground">
                  A empresa está ativa e operacional
                </div>
              </div>
              <Badge variant="default">
                {company?.status || 'Ativo'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}